import { type FC, useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { AccessCodeDurationUnit } from '../../../modules/enums/types';
import { useNotifications } from '../../../modules/hooks';
import { useAccessCodes } from '../../Dashboards/TemporaryAccessCodes/useAccessCodes';

interface ExtendModalProps {
  show: boolean;
  uuid?: string;
  handleClose: () => void;
  onSaved?: () => void;
}

const DURATION_UNIT_OPTIONS: { value: AccessCodeDurationUnit; label: string }[] = [
  { value: AccessCodeDurationUnit.MINUTES, label: 'Minutes' },
  { value: AccessCodeDurationUnit.HOURS, label: 'Hours' },
  { value: AccessCodeDurationUnit.DAYS, label: 'Days' },
  { value: AccessCodeDurationUnit.WEEKS, label: 'Weeks' },
];

/** Extends an access code's window via the time-frame route (duration adds to the current end). */
export const ExtendModal: FC<ExtendModalProps> = ({
  show,
  uuid,
  handleClose,
  onSaved,
}) => {
  const { setSimpleToasts } = useNotifications()!;
  const accessCodes = useAccessCodes();
  const [duration, setDuration] = useState('1');
  const [durationUnit, setDurationUnit] = useState<AccessCodeDurationUnit>(
    AccessCodeDurationUnit.DAYS,
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setDuration('1');
    setDurationUnit(AccessCodeDurationUnit.DAYS);
  }, [show]);

  const handleSave = async () => {
    if (!uuid) return;
    const durationValue = Number(duration);
    if (!durationValue || durationValue <= 0) {
      setSimpleToasts({
        type: 'danger',
        message: 'Please enter a valid duration',
        show: true,
      });
      return;
    }
    setSaving(true);
    try {
      await accessCodes.updateTimeFrame(uuid, { duration: durationValue, durationUnit });
      setSimpleToasts({ type: 'success', message: 'Access code extended', show: true });
      onSaved?.();
      handleClose();
    } catch {
      setSimpleToasts({
        type: 'danger',
        message: 'Failed to extend access code',
        show: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Extend access code</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">
          The duration is added on top of the code's current end date.
        </p>
        <Row>
          <Col xs={6}>
            <Form.Group>
              <Form.Label>Duration</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={duration}
                onChange={(event) => setDuration(event.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group>
              <Form.Label>Unit</Form.Label>
              <Form.Select
                value={durationUnit}
                onChange={(event) =>
                  setDurationUnit(event.target.value as AccessCodeDurationUnit)
                }
              >
                {DURATION_UNIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button className="cancel-button" onClick={handleClose}>
          Cancel
        </Button>
        <Button className="save-button" onClick={handleSave} disabled={saving}>
          {saving ? 'Extending…' : 'Extend'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
